/**
 * MoneynessC Utilities
 * Provides ITM/ATM/OTM categorization for options filtering
 * This replaces the numerical moneyness filter with categorical moneyness
 */

import { ScannerData } from '../types/ScannerData';

export type MoneynessC = 'ITM' | 'ATM' | 'OTM' | 'Unknown';

/**
 * Calculate MoneynessC (ITM/ATM/OTM) for an option
 */
export const calculateMoneynessC = (
  strike: number,
  underlyingPrice: number,
  optionType: string
): MoneynessC => {
  try {
    if (!strike || !underlyingPrice || underlyingPrice <= 0) {
      return 'Unknown';
    }
    
    const ratio = strike / underlyingPrice;
    const optionTypeUpper = String(optionType).toUpperCase();
    
    if (optionTypeUpper === 'C' || optionTypeUpper === 'CALL') {
      // For calls: ITM when strike < underlying, OTM when strike > underlying
      if (ratio < 0.95) return 'ITM';
      if (ratio > 1.05) return 'OTM';
      return 'ATM';
    } else if (optionTypeUpper === 'P' || optionTypeUpper === 'PUT') {
      // For puts: ITM when strike > underlying, OTM when strike < underlying
      if (ratio > 1.05) return 'ITM';
      if (ratio < 0.95) return 'OTM';
      return 'ATM';
    }
    
    return 'Unknown';
  } catch (error) {
    console.error('Error calculating MoneynessC:', error);
    return 'Unknown';
  }
};

/**
 * Get MoneynessC for a ScannerData item
 */
export const getMoneynessC = (item: ScannerData): MoneynessC => {
  const strike = parseFloat(String(item.Strike || '0'));
  const underlyingPrice = parseFloat(String(item['Underlying Price'] || '0'));
  const optionType = item['Option Type'] || '';
  
  return calculateMoneynessC(strike, underlyingPrice, optionType);
};

/**
 * Get available MoneynessC options from data
 */
export const getAvailableMoneynessC = (data: ScannerData[]): MoneynessC[] => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }
  
  const moneynessSet = new Set<MoneynessC>();
  
  data.forEach(item => {
    const moneynessC = getMoneynessC(item);
    if (moneynessC !== 'Unknown') {
      moneynessSet.add(moneynessC);
    }
  });
  
  return Array.from(moneynessSet).sort();
};

/**
 * Filter data by MoneynessC
 */
export const filterByMoneynessC = (
  data: ScannerData[],
  selectedMoneynessC: MoneynessC | 'all'
): ScannerData[] => {
  if (selectedMoneynessC === 'all') {
    return data;
  }
  
  return data.filter(item => {
    const moneynessC = getMoneynessC(item);
    return moneynessC === selectedMoneynessC;
  });
};

/**
 * Get MoneynessC display name
 */
export const getMoneynessCDisplayName = (moneynessC: MoneynessC): string => {
  switch (moneynessC) {
    case 'ITM': return 'ITM (In The Money)';
    case 'ATM': return 'ATM (At The Money)';
    case 'OTM': return 'OTM (Out The Money)';
    case 'Unknown': return 'Unknown';
    default: return moneynessC;
  }
};

/**
 * Get MoneynessC display name in Arabic
 */
export const getMoneynessCDisplayNameArabic = (moneynessC: MoneynessC): string => {
  switch (moneynessC) {
    case 'ITM': return 'في المال';
    case 'ATM': return 'عند المال';
    case 'OTM': return 'خارج المال';
    case 'Unknown': return 'غير معروف';
    default: return moneynessC;
  }
};

/**
 * Get MoneynessC color for UI
 */
export const getMoneynessCColor = (moneynessC: MoneynessC): string => {
  switch (moneynessC) {
    case 'ITM': return '#28A745'; // Green
    case 'ATM': return '#FFC107'; // Yellow
    case 'OTM': return '#DC3545'; // Red
    case 'Unknown': return '#6C757D'; // Gray
    default: return '#6C757D';
  }
};

/**
 * Get MoneynessC description
 */
export const getMoneynessCDescription = (moneynessC: MoneynessC): string => {
  switch (moneynessC) {
    case 'ITM': return 'In The Money - Option has intrinsic value';
    case 'ATM': return 'At The Money - Strike price equals underlying price';
    case 'OTM': return 'Out The Money - Option has no intrinsic value';
    case 'Unknown': return 'Moneyness cannot be determined';
    default: return '';
  }
}; 